import React, { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Shared building blocks for the photo gallery (mosaic grid and lightbox).
 * Not a registered component — has no `contentSchema`, never appears in
 * `registry.ts`, and is not re-exported from `components/index.ts`.
 *
 * The schema linter skips files without a contentSchema, so this helper
 * sits safely alongside the registered gallery sections without tripping
 * any rules (every photo is rendered through VibesitesImage).
 */

export interface GalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string;
}

/** Classify a gallery photo by its title — used to split community vs unit shots. */
export function classifyPhoto(photo: GalleryImage): 'apartment' | 'community' | 'other' {
  const title = (photo.title || '').toLowerCase().trim();
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return 'other';
}

function urlPathText(url?: string): string {
  if (!url) return '';
  try {
    return new URL(url).pathname.toLowerCase().replace(/[_-]+/g, ' ');
  } catch {
    return url.toLowerCase().split('?')[0].replace(/[_-]+/g, ' ');
  }
}

/** Human-authored labels — preferred over CDN path noise for subtype sorting. */
function photoLabelText(photo: GalleryImage): string {
  return [photo.title, photo.description, photo.alt_text]
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .toLowerCase();
}

function photoSearchText(photo: GalleryImage): string {
  return [
    photo.title,
    photo.description,
    photo.alt_text,
    urlPathText(photo.image),
    urlPathText(photo.original_url),
    photo.source_page_urls,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .toLowerCase();
}

type KeywordMatch = {
  rank: number;
  /** Orders like images inside a rank (keyword position / canonical key). */
  subRank: number;
  keyword: string;
  length: number;
};

/** Collapse label variants so e.g. pool / swimming pool sort as one cluster. */
const KEYWORD_CANONICAL: Record<string, string> = {
  'swimming pool': 'pool',
  'indoor swimming': 'indoor pool',
  'indoor pool': 'indoor pool',
  pool: 'pool',
  barbecue: 'bbq',
  grilling: 'bbq',
  grill: 'bbq',
  firepit: 'fire pit',
  'sun deck': 'sundeck',
  greenspace: 'green space',
  'club house': 'clubhouse',
  'pool table': 'billiard',
  pooltable: 'billiard',
  billiards: 'billiard',
  'fitness center': 'fitness',
  gym: 'fitness',
  workout: 'fitness',
  'weight room': 'fitness',
  coworking: 'cowork',
  'cyber cafe': 'business center',
  'computer lab': 'business center',
  conference: 'business center',
  'washer and dryer': 'laundry',
  washer: 'laundry',
  dryer: 'laundry',
  laundry: 'laundry',
  'w/d': 'laundry',
  'walk-in': 'closet',
  wardrobe: 'closet',
  livingroom: 'living',
  'living room': 'living',
  'great room': 'living',
  'family room': 'living',
  dining: 'living',
  'bath room': 'bathroom',
  bathtub: 'bathroom',
  shower: 'bathroom',
  vanity: 'bathroom',
  restroom: 'bathroom',
  'powder room': 'bathroom',
  bath: 'bathroom',
};

/**
 * Pick the subtype with the longest matching keyword so specific labels win
 * (e.g. "bathroom" over "bedroom", "clubhouse" over "building").
 */
function bestKeywordMatch(text: string, groups: string[][]): KeywordMatch {
  let best: KeywordMatch = {
    rank: groups.length,
    subRank: 0,
    keyword: '',
    length: 0,
  };

  for (let i = 0; i < groups.length; i += 1) {
    for (let j = 0; j < groups[i].length; j += 1) {
      const keyword = groups[i][j];
      if (!text.includes(keyword)) continue;
      if (keyword.length < best.length) continue;
      if (keyword.length === best.length && i > best.rank) continue;
      if (keyword.length === best.length && i === best.rank && j >= best.subRank) continue;
      best = { rank: i, subRank: j, keyword, length: keyword.length };
    }
  }

  return best;
}

function canonicalKeyword(keyword: string): string {
  return KEYWORD_CANONICAL[keyword] || keyword;
}

/** Remove billiard "pool table" phrases entirely (for swimming-pool detection). */
function textWithoutPoolTables(text: string): string {
  return text.replace(/pool\s*tables?/g, ' ').replace(/pooltables?/g, ' ');
}

/**
 * Rewrite billiard "pool table" phrases to "billiard" so keyword matching:
 * 1) does not treat them as swimming "pool", and
 * 2) cannot substring-match "indoor pool" inside "indoor pool table".
 */
function normalizePoolTableText(text: string): string {
  return text.replace(/pool\s*tables?/g, ' billiard ').replace(/pooltables?/g, ' billiard ');
}

/** True for swimming pools — not billiard / pool-table rooms. */
function isPoolText(text: string): boolean {
  const swimmingText = textWithoutPoolTables(text);
  return swimmingText.includes('pool') || swimmingText.includes('swimming');
}

/** Indoor pools must stay together — "indoor swimming pool" used to split across outdoor/indoor. */
function isIndoorPoolText(text: string): boolean {
  if (!isPoolText(text)) return false;
  const swimmingText = textWithoutPoolTables(text);
  if (swimmingText.includes('outdoor pool') || swimmingText.includes('outdoor swimming')) {
    return false;
  }
  return (
    swimmingText.includes('indoor pool') ||
    swimmingText.includes('indoor swimming') ||
    (swimmingText.includes('indoor') && isPoolText(text))
  );
}

/** Unit interiors — kitchen → living → office → bedroom → bath → laundry → closet → patio. */
const APARTMENT_SUBTYPE_KEYWORDS: string[][] = [
  ['kitchen', 'cooktop', 'stove', 'island'],
  ['living room', 'livingroom', 'living', 'great room', 'family room', 'dining'],
  ['office', 'den', 'study', 'flex room', 'flex space', 'work from home'],
  ['bedroom', 'bed room', 'master bedroom', 'primary bedroom', 'master suite', 'primary suite'],
  // Longest-keyword match still classifies "bathroom" over "bedroom" when both appear.
  ['bathroom', 'bath room', 'master bath', 'primary bath', 'guest bath', 'full bath', 'half bath', 'powder room', 'restroom', 'vanity', 'bathtub', 'shower', 'bath'],
  ['washer and dryer', 'washer', 'dryer', 'laundry', 'w/d', 'laundry room'],
  ['closet', 'wardrobe', 'walk-in'],
  ['patio', 'paito', 'balcony', 'terrace', 'deck'],
];

/**
 * Community / amenity shots — exterior → aerial → outdoor → indoor pool →
 * clubhouse → fitness → business center → spa → pet → other indoor.
 * Unmatched community photos default to the generic indoor catch-all.
 */
const COMMUNITY_OUTDOOR = 2;
/** Last establishing rank (exterior / aerial / outdoor) before named amenities. */
const COMMUNITY_ESTABLISHING_MAX = COMMUNITY_OUTDOOR;
const COMMUNITY_INDOOR_POOL = 3;
const COMMUNITY_INDOOR_OTHER = 9;
/**
 * Short amenity tokens ("lounge", "lobby") must not pull outdoor shots into
 * clubhouse when a longer establishing keyword already matched.
 */
const MIN_NAMED_AMENITY_OVERRIDE_LEN = 7;

const COMMUNITY_SUBTYPE_KEYWORDS: string[][] = [
  // Building exterior first — establish the property before amenities.
  [
    'building exterior',
    'property exterior',
    'exterior',
    'facade',
    'façade',
    'main entrance',
    'curb appeal',
    'street view',
    'building front',
  ],
  ['aerial', 'drone', 'bird\'s eye', 'birds eye', 'birdseye', 'overhead', 'skyline'],
  // Outdoor: pool keywords first so outdoor pools cluster, then other amenities.
  [
    'swimming pool',
    'pool',
    'outdoor amenit',
    'courtyard',
    'playground',
    'dog park',
    'pet park',
    'bbq',
    'barbecue',
    'grilling',
    'grill',
    'fire pit',
    'firepit',
    'picnic',
    'gazebo',
    'pergola',
    'cabana',
    'hot tub',
    'jacuzzi',
    'splash',
    'tennis',
    'pickleball',
    'basketball',
    'sport court',
    'sundeck',
    'sun deck',
    'green space',
    'greenspace',
    'garden',
    'fountain',
    'outdoor',
  ],
  // Indoor pools — kept as their own group so they never mix with outdoor pools
  // or generic "indoor" catch-all shots.
  ['indoor pool', 'indoor swimming', 'lap pool'],
  [
    'clubhouse',
    'club house',
    'resident club',
    'amenity center',
    'lounge',
    'lobby',
    'game room',
    'theater',
    // Billiards — "pool table" must beat bare outdoor "pool" (longest-keyword match).
    'pool table',
    'pooltable',
    'billiards',
    'billiard',
    'leasing',
    'concierge',
    'package',
    'mail room',
    'mailroom',
  ],
  ['fitness center', 'fitness', 'gym', 'yoga', 'workout', 'weight room'],
  ['business center', 'conference', 'cowork', 'coworking', 'cyber cafe', 'computer lab'],
  ['spa', 'sauna', 'steam room', 'massage'],
  [
    'pet spa',
    'pet wash',
    'dog wash',
    'pet grooming',
    'pet care',
    'pet station',
    'pet amenit',
    'pet',
  ],
  // Remaining indoor / generic amenity shots (do not include bare "indoor" —
  // that steals indoor pool photos; pools are handled above).
  ['indoor amenit', 'media room', 'amenit'],
];

const BUCKET_ORDER = { community: 0, apartment: 1, other: 2 } as const;

/**
 * Longest-keyword alone lets "building exterior" / "outdoor amenit" steal
 * clubhouse & fitness shots. Prefer a clear named-amenity label when present.
 */
function preferNamedAmenityOverEstablishing(
  match: KeywordMatch,
  text: string,
): KeywordMatch {
  if (match.rank > COMMUNITY_ESTABLISHING_MAX) return match;

  const amenityGroups = COMMUNITY_SUBTYPE_KEYWORDS.slice(COMMUNITY_INDOOR_POOL);
  const amenityMatch = bestKeywordMatch(text, amenityGroups);
  if (amenityMatch.rank >= amenityGroups.length) return match;
  if (amenityMatch.length < MIN_NAMED_AMENITY_OVERRIDE_LEN) return match;

  return {
    rank: amenityMatch.rank + COMMUNITY_INDOOR_POOL,
    subRank: amenityMatch.subRank,
    keyword: amenityMatch.keyword,
    length: amenityMatch.length,
  };
}

/** Raw community match — no default rank (used for apartment vs community inference). */
function communityKeywordMatchRaw(text: string): KeywordMatch {
  if (isIndoorPoolText(text)) {
    return {
      rank: COMMUNITY_INDOOR_POOL,
      subRank: 0,
      keyword: 'indoor pool',
      length: 11,
    };
  }

  // Pool + named amenity (e.g. "clubhouse pool deck") — keep the amenity group.
  // Strip pool-table phrases so billiards wording cannot pull a real swimming-pool
  // shot out of the outdoor bucket when both appear in the same caption.
  if (isPoolText(text)) {
    const amenitySearch = textWithoutPoolTables(text);
    const amenityGroups = COMMUNITY_SUBTYPE_KEYWORDS.slice(COMMUNITY_INDOOR_POOL + 1);
    const amenityMatch = bestKeywordMatch(amenitySearch, amenityGroups);
    if (
      amenityMatch.rank < amenityGroups.length &&
      amenityMatch.length >= MIN_NAMED_AMENITY_OVERRIDE_LEN
    ) {
      return {
        rank: amenityMatch.rank + COMMUNITY_INDOOR_POOL + 1,
        subRank: amenityMatch.subRank,
        keyword: amenityMatch.keyword,
        length: amenityMatch.length,
      };
    }
    return {
      rank: COMMUNITY_OUTDOOR,
      subRank: 0,
      keyword: 'pool',
      length: 4,
    };
  }

  // Rewrite pool-table → billiard so "indoor pool table" does not match "indoor pool".
  const normalized = normalizePoolTableText(text);
  return preferNamedAmenityOverEstablishing(
    bestKeywordMatch(normalized, COMMUNITY_SUBTYPE_KEYWORDS),
    normalized,
  );
}

/**
 * Community subtype for sorting: prefer title/alt/description matches so CDN
 * paths like "building-exterior-clubhouse.jpg" cannot reshuffle labeled shots.
 */
function communityKeywordMatchForPhoto(photo: GalleryImage): KeywordMatch {
  const labelText = photoLabelText(photo);
  if (labelText.trim()) {
    const labelMatch = communityKeywordMatchRaw(labelText);
    if (labelMatch.rank < COMMUNITY_SUBTYPE_KEYWORDS.length) {
      return labelMatch;
    }
  }

  const match = communityKeywordMatchRaw(photoSearchText(photo));
  if (match.rank >= COMMUNITY_SUBTYPE_KEYWORDS.length) {
    return {
      rank: COMMUNITY_INDOOR_OTHER,
      subRank: 0,
      keyword: '',
      length: 0,
    };
  }
  return match;
}

/**
 * Infer apartment vs community when the title is not already labeled.
 * Room-type keywords lean unit; amenity/exterior/aerial lean community.
 */
export function inferPhotoBucket(photo: GalleryImage): 'apartment' | 'community' | 'other' {
  const labeled = classifyPhoto(photo);
  if (labeled !== 'other') return labeled;

  const text = photoSearchText(photo);
  if (!text.trim()) return 'other';

  // Pool shots are always community amenities.
  if (isPoolText(text)) return 'community';

  const apartmentHit = bestKeywordMatch(text, APARTMENT_SUBTYPE_KEYWORDS);
  const communityHit = communityKeywordMatchRaw(text);
  const apartmentMatched = apartmentHit.rank < APARTMENT_SUBTYPE_KEYWORDS.length;
  const communityMatched = communityHit.rank < COMMUNITY_SUBTYPE_KEYWORDS.length;

  if (apartmentMatched && !communityMatched) return 'apartment';
  if (communityMatched && !apartmentMatched) return 'community';
  if (apartmentMatched && communityMatched) {
    if (communityHit.length > apartmentHit.length) return 'community';
    return 'apartment';
  }
  return 'other';
}

function subtypeSortKeys(
  photo: GalleryImage,
  bucket: 'apartment' | 'community' | 'other',
): { subtypeRank: number; subRank: number; clusterKey: string } {
  const text = photoSearchText(photo);

  if (bucket === 'apartment') {
    const match = bestKeywordMatch(text, APARTMENT_SUBTYPE_KEYWORDS);
    return {
      subtypeRank: match.rank,
      subRank: match.subRank,
      clusterKey: canonicalKeyword(match.keyword),
    };
  }

  if (bucket === 'community') {
    const match = communityKeywordMatchForPhoto(photo);
    return {
      subtypeRank: match.rank,
      subRank: match.subRank,
      clusterKey: canonicalKeyword(match.keyword),
    };
  }

  return { subtypeRank: 0, subRank: 0, clusterKey: '' };
}

/**
 * Sort gallery photos: community/amenity shots first (exterior → aerial →
 * outdoor → indoor pool → clubhouse → fitness → business center → spa →
 * pet → other indoor), then unit interiors (grouped by room type).
 * Within each group, like labels cluster together. Stable for equal ranks.
 */
export function sortGalleryImages(images: GalleryImage[]): GalleryImage[] {
  return images
    .map((image, index) => {
      const bucket = inferPhotoBucket(image);
      const keys = subtypeSortKeys(image, bucket);
      return {
        image,
        index,
        bucketRank: BUCKET_ORDER[bucket],
        subtypeRank: keys.subtypeRank,
        subRank: keys.subRank,
        clusterKey: keys.clusterKey,
      };
    })
    .sort((a, b) => {
      if (a.bucketRank !== b.bucketRank) return a.bucketRank - b.bucketRank;
      if (a.subtypeRank !== b.subtypeRank) return a.subtypeRank - b.subtypeRank;
      if (a.subRank !== b.subRank) return a.subRank - b.subRank;
      if (a.clusterKey !== b.clusterKey) return a.clusterKey.localeCompare(b.clusterKey);
      return a.index - b.index;
    })
    .map((entry) => entry.image);
}

interface MobileGalleryCarouselProps {
  images: GalleryImage[];
  onSelect: (index: number) => void;
}

/** Single-image swipeable carousel shown below the `sm` breakpoint. */
export function MobileGalleryCarousel({ images, onSelect }: MobileGalleryCarouselProps) {
  const [current, setCurrent] = useState(0);
  const count = images.length;
  const touchStartX = useRef<number | null>(null);

  const advance = useCallback(
    (dir: 1 | -1) => {
      if (count === 0) return;
      setCurrent((p) => (p + dir + count) % count);
    },
    [count],
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(dx) < 40) return;
      if (dx < 0) advance(1);
      else advance(-1);
    },
    [advance],
  );

  if (count === 0) return null;

  const safeCurrent = Math.min(current, count - 1);

  return (
    <div
      className="gallery-mobile-carousel"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* All slides stay mounted so they decode up front; only the active one is opaque and clickable. */}
      {images.map((img, i) => (
        <div
          key={img.image || i}
          className={`gallery-mobile-carousel__slide gallery-item${i === safeCurrent ? ' is-active' : ''}`}
          aria-hidden={i === safeCurrent ? undefined : true}
          onClick={i === safeCurrent ? () => onSelect(safeCurrent) : undefined}
        >
          <VibesitesImage
            url={img.image}
            alt={img.alt_text || img.title || ''}
            className="w-full h-full object-cover"
            sizes="100vw"
            priority={i === 0}
          />
        </div>
      ))}

      {count > 1 && (
        <>
          <button
            type="button"
            className="gallery-mobile-carousel__arrow gallery-mobile-carousel__arrow--prev"
            onClick={(e) => {
              e.stopPropagation();
              advance(-1);
            }}
            aria-label="Previous image"
          >
            <LightboxChevron direction="prev" />
          </button>
          <button
            type="button"
            className="gallery-mobile-carousel__arrow gallery-mobile-carousel__arrow--next"
            onClick={(e) => {
              e.stopPropagation();
              advance(1);
            }}
            aria-label="Next image"
          >
            <LightboxChevron direction="next" />
          </button>

          <div className="gallery-mobile-carousel__dots" aria-hidden="true">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`gallery-mobile-carousel__dot${i === safeCurrent ? ' is-active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrent(i);
                }}
                aria-label={`Show image ${i + 1}`}
              />
            ))}
          </div>

          <div className="gallery-mobile-carousel__counter" aria-live="polite">
            {safeCurrent + 1} / {count}
          </div>
        </>
      )}
    </div>
  );
}

interface GalleryGridProps {
  images: GalleryImage[];
  onSelect: (index: number) => void;
}

/** 16:9 tile grid (2 cols tablet, 3 cols desktop) with a swipeable carousel on mobile. */
export function GalleryGrid({ images, onSelect }: GalleryGridProps) {
  if (images.length === 0) return null;
  return (
    <>
      <div className="gallery-grid hidden sm:grid grid-cols-2 md:grid-cols-3 gap-3">
        {images.map((img, index) => (
          <div
            key={img.image || index}
            className="aspect-video overflow-hidden group cursor-pointer gallery-item"
            onClick={() => onSelect(index)}
          >
            <VibesitesImage
              url={img.image}
              alt={img.alt_text || img.title || ''}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              sizes="(min-width: 768px) 30vw, 45vw"
              priority={index === 0}
            />
          </div>
        ))}
      </div>

      <div className="sm:hidden">
        <MobileGalleryCarousel images={images} onSelect={onSelect} />
      </div>
    </>
  );
}

interface GalleryLightboxProps {
  images: GalleryImage[];
  selectedIndex: number | null;
  onClose: () => void;
  onChange: (index: number | null) => void;
}

function LightboxChevron({ direction }: { direction: 'prev' | 'next' }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {direction === 'prev' ? (
        <path d="M15 18l-6-6 6-6" />
      ) : (
        <path d="M9 18l6-6-6-6" />
      )}
    </svg>
  );
}

export function GalleryLightbox({
  images,
  selectedIndex,
  onClose,
  onChange,
}: GalleryLightboxProps) {
  const total = images.length;

  const goNext = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex + 1) % total);
    },
    [onChange, selectedIndex, total],
  );

  const goPrev = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex - 1 + total) % total);
    },
    [onChange, selectedIndex, total],
  );

  useEffect(() => {
    if (selectedIndex === null) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedIndex]);

  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedIndex, goNext, goPrev, onClose]);

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy = e.changedTouches[0].clientY - touchStartY.current;
      touchStartX.current = null;
      touchStartY.current = null;

      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  const handleStageClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const stopClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;
  if (!selectedImage || typeof document === 'undefined') return null;

  // Portaled to <body> so third-party widgets (e.g. AudioEye) can't sit above it.
  return createPortal(
    <div
      className="gallery-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery viewer"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="gallery-lightbox__shell">
        <div className="gallery-lightbox__toolbar">
          {total > 1 ? (
            <p className="gallery-lightbox__counter" aria-live="polite">
              {(selectedIndex ?? 0) + 1} <span className="gallery-lightbox__counter-sep">/</span>{' '}
              {total}
            </p>
          ) : (
            <span aria-hidden="true" />
          )}

          <button
            type="button"
            onClick={onClose}
            className="gallery-lightbox__close"
            aria-label="Close gallery"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          className="gallery-lightbox__stage"
          onClick={handleStageClick}
        >
          {total > 1 && (
            <button
              type="button"
              onClick={goPrev}
              className="gallery-lightbox__nav gallery-lightbox__nav--prev gallery-lightbox__nav--desktop"
              aria-label="Previous photo"
            >
              <LightboxChevron direction="prev" />
            </button>
          )}

          <figure className="gallery-lightbox__figure">
            <div className="gallery-lightbox__img-wrap">
              <div className="gallery-lightbox__img-frame" onClick={stopClick}>
                <VibesitesImage
                  url={selectedImage.image}
                  alt={selectedImage.alt_text || selectedImage.title || 'Property photo'}
                  className="gallery-lightbox__img"
                  sizes="(min-width: 1460px) 1400px, 96vw"
                />
              </div>
            </div>
          </figure>

          {total > 1 && (
            <button
              type="button"
              onClick={goNext}
              className="gallery-lightbox__nav gallery-lightbox__nav--next gallery-lightbox__nav--desktop"
              aria-label="Next photo"
            >
              <LightboxChevron direction="next" />
            </button>
          )}
        </div>

        {total > 1 && (
          <div className="gallery-lightbox__mobile-nav">
            <button
              type="button"
              onClick={goPrev}
              className="gallery-lightbox__nav gallery-lightbox__nav--prev gallery-lightbox__nav--mobile"
              aria-label="Previous photo"
            >
              <LightboxChevron direction="prev" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="gallery-lightbox__nav gallery-lightbox__nav--next gallery-lightbox__nav--mobile"
              aria-label="Next photo"
            >
              <LightboxChevron direction="next" />
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Shared CSS for the gallery mosaic and lightbox.
 */
export function GallerySharedStyles() {
  return (
    <style>{`
      .gallery-constrained {
        width: 90vw;
        max-width: 90vw;
        margin-left: auto;
        margin-right: auto;
        padding-left: 0;
        padding-right: 0;
      }

      @media (max-width: 639px) {
        .gallery-constrained {
          width: 100vw;
          max-width: 100vw;
          margin-left: 0;
          margin-right: 0;
        }
      }

      .gallery-item {
        animation: gallery-fade-in 0.35s ease both;
      }

      @keyframes gallery-fade-in {
        from { opacity: 0; transform: scale(0.97); }
        to { opacity: 1; transform: scale(1); }
      }

      @media (prefers-reduced-motion: reduce) {
        .gallery-item { animation: none; }
      }

      /* ── Mobile single-image carousel ─────────────────────── */
      .gallery-mobile-carousel {
        position: relative;
        width: 100%;
        aspect-ratio: 16 / 9;
        overflow: hidden;
        background: #000;
        touch-action: pan-y;
        -webkit-user-select: none;
        user-select: none;
      }

      .gallery-mobile-carousel__slide {
        position: absolute;
        inset: 0;
        cursor: pointer;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.25s ease;
      }
      .gallery-mobile-carousel__slide.is-active {
        opacity: 1;
        pointer-events: auto;
        z-index: 1;
      }

      .gallery-mobile-carousel__arrow {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: none;
        background: rgba(0, 0, 0, 0.45);
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        z-index: 2;
        padding: 0;
        transition: background 0.2s;
      }
      .gallery-mobile-carousel__arrow:active {
        background: rgba(0, 0, 0, 0.7);
      }
      .gallery-mobile-carousel__arrow--prev { left: 0.5rem; }
      .gallery-mobile-carousel__arrow--next { right: 0.5rem; }

      .gallery-mobile-carousel__dots {
        position: absolute;
        bottom: 0.6rem;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 6px;
        z-index: 2;
        max-width: calc(100% - 6rem);
        flex-wrap: wrap;
        justify-content: center;
      }

      .gallery-mobile-carousel__dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        border: 1.5px solid rgba(255, 255, 255, 0.7);
        background: transparent;
        padding: 0;
        cursor: pointer;
        transition: background 0.2s, border-color 0.2s;
      }
      .gallery-mobile-carousel__dot.is-active {
        background: #fff;
        border-color: #fff;
      }

      .gallery-mobile-carousel__counter {
        position: absolute;
        top: 0.5rem;
        right: 0.6rem;
        font-family: var(--vibe-font-body);
        font-size: 0.75rem;
        letter-spacing: 0.04em;
        color: #fff;
        background: rgba(0, 0, 0, 0.45);
        padding: 0.15rem 0.5rem;
        border-radius: 999px;
        z-index: 2;
        pointer-events: none;
      }

      /* ── Lightbox ───────────────────────────────────────────── */
      .gallery-lightbox {
        position: fixed;
        inset: 0;
        z-index: 2147483647;
        display: flex;
        align-items: stretch;
        justify-content: center;
        background: rgba(0, 0, 0, 0.94);
        animation: gallery-lightbox-in 0.2s ease both;
      }

      @keyframes gallery-lightbox-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @media (prefers-reduced-motion: reduce) {
        .gallery-lightbox { animation: none; }
      }

      .gallery-lightbox__shell {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 100vw;
        height: 100dvh;
      }

      .gallery-lightbox__toolbar,
      .gallery-lightbox__stage,
      .gallery-lightbox__mobile-nav,
      .gallery-lightbox__nav,
      .gallery-lightbox__close,
      .gallery-lightbox__img-frame {
        pointer-events: auto;
      }

      .gallery-lightbox__toolbar {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 0.875rem 1rem 0.5rem;
      }

      .gallery-lightbox__counter {
        margin: 0;
        font-family: var(--vibe-font-body);
        font-size: 0.875rem;
        font-weight: 500;
        letter-spacing: 0.06em;
        color: rgba(255, 255, 255, 0.88);
      }

      .gallery-lightbox__counter-sep {
        color: rgba(255, 255, 255, 0.45);
      }

      .gallery-lightbox__close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        border: none;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
        cursor: pointer;
        transition: background 0.15s ease, transform 0.15s ease;
      }

      .gallery-lightbox__close:hover {
        background: rgba(255, 255, 255, 0.18);
      }

      .gallery-lightbox__close:active {
        transform: scale(0.96);
      }

      .gallery-lightbox__stage {
        flex: 1;
        min-height: 0;
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: 0.5rem;
        padding: 0 0.5rem 1rem;
        cursor: pointer;
        touch-action: pan-y;
      }

      .gallery-lightbox__figure {
        margin: 0;
        min-width: 0;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.75rem;
        pointer-events: none;
      }

      .gallery-lightbox__img-wrap {
        flex: 1;
        min-height: 0;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      }

      .gallery-lightbox__img-frame {
        display: inline-flex;
        max-width: 100%;
        max-height: 100%;
        cursor: default;
      }

      .gallery-lightbox__img {
        display: block;
        max-width: min(96vw, 1400px);
        max-height: calc(100dvh - 5.5rem);
        width: auto;
        height: auto;
        object-fit: contain;
        border-radius: 2px;
        box-shadow: 0 12px 48px rgba(0, 0, 0, 0.45);
      }

      .gallery-lightbox__nav {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.75rem;
        height: 2.75rem;
        border: 1px solid rgba(255, 255, 255, 0.14);
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.08);
        color: #fff;
        cursor: pointer;
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
        -webkit-user-select: none;
        user-select: none;
      }

      .gallery-lightbox__nav:hover {
        background: rgba(255, 255, 255, 0.16);
        border-color: rgba(255, 255, 255, 0.28);
      }

      .gallery-lightbox__nav:active {
        transform: scale(0.96);
      }

      .gallery-lightbox__mobile-nav {
        display: none;
      }

      .gallery-lightbox__nav--desktop {
        cursor: pointer;
      }

      @media (min-width: 768px) {
        .gallery-lightbox__toolbar {
          padding: 1rem 1.25rem 0.625rem;
        }

        .gallery-lightbox__stage {
          gap: 1rem;
          padding: 0 1rem 1.25rem;
        }

        .gallery-lightbox__nav {
          width: 3rem;
          height: 3rem;
        }

        .gallery-lightbox__img {
          max-height: calc(100dvh - 6rem);
        }
      }

      @media (max-width: 639px) {
        .gallery-lightbox__stage {
          grid-template-columns: minmax(0, 1fr);
          grid-template-rows: 1fr;
          gap: 0;
          padding: 0;
        }

        .gallery-lightbox__figure {
          width: 100%;
          padding: 0;
          align-items: center;
          justify-content: center;
        }

        .gallery-lightbox__img-wrap {
          width: 100%;
          align-items: center;
          justify-content: center;
        }

        .gallery-lightbox__img-frame {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-height: 100%;
        }

        .gallery-lightbox__nav--desktop {
          display: none;
        }

        .gallery-lightbox__mobile-nav {
          display: flex;
          flex-shrink: 0;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 0.5rem 1.25rem calc(0.875rem + env(safe-area-inset-bottom, 0px));
        }

        .gallery-lightbox__nav--mobile {
          width: 3.25rem;
          height: 3.25rem;
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.16);
        }

        .gallery-lightbox__nav--mobile:active {
          transform: scale(0.96);
        }

        .gallery-lightbox__img {
          display: block;
          width: 100%;
          max-width: 100%;
          height: auto;
          max-height: calc(100dvh - 8.5rem);
          margin: 0 auto;
          border-radius: 0;
          box-shadow: none;
          object-fit: contain;
        }
      }
    `}</style>
  );
}
